import { useContext, useEffect, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum } from '@/types/common';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { CHARGEBEE_WALLET_BUTTON_IDS } from '@/constants/chargebee';
import { chargebeeService, type ChargebeeWalletButton } from '@/services/chargebeeService';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import { useLatestRef } from '@/hooks/useLatestRef';
import { useChargebeeWalletZipGate } from '@/hooks/useChargebeeWalletZipGate';
import { finalizeAlreadyAuthorizedWalletPayment, isChargebeeUserClosedError } from '@/utils/chargebeeWallets';
import {
  isChargebeePaymentIntentAuthorizedError,
  isHydratedChargebeePaymentIntent,
} from '@/utils/chargebeePaymentIntent';
import { logError } from '@/utils/logger';
import { useTranslate } from '@/hooks/useTranslate';

/**
 * Chargebee Google Pay (subscriptions only). Chargebee mounts and owns the
 * button + payment sheet; we wrap its mount target in the same
 * `WalletButtonShell` + brand placeholder every other wallet uses, so sizing,
 * loading overlay and ready-pulse are identical. Every SDK call goes through
 * {@link chargebeeService}. The finalize runs through `runPayment` →
 * googlePayPaymentHandler's CHARGEBEE case, keeping the dispatch matrix
 * authoritative.
 */
export const ChargebeeGooglePayForm = ({ onReady, onInvalidTaxClick }: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const { subscription } = context;
  const { t } = useTranslate();
  const [state, setState] = useState<WalletReadyState>('loading');
  const buttonRef = useRef<ChargebeeWalletButton | null>(null);
  const initializedRef = useRef(false);
  const contextRef = useLatestRef(context);
  const onReadyRef = useLatestRef(onReady);
  // The mount effect runs once, so `t` has to be read through a ref: its
  // identity churns as i18n resources land, and a captured copy would still be
  // the pre-load one that echoes raw keys back as the error message.
  const tRef = useLatestRef(t);
  const cbInstance = subscription?.cbInstance;
  // Chargebee validates the intent inside `setPaymentIntent`, so mounting
  // before `useChargebeePaymentIntent` has expanded the summary's `{ id }`
  // would permanently mark the wallet unavailable.
  const isPaymentIntentReady = isHydratedChargebeePaymentIntent(subscription?.paymentIntent);
  const isPaymentIntentUnavailable = subscription?.isPaymentIntentUnavailable ?? false;
  const { isBlocked: isBlockedForZipCode, blockClick } = useChargebeeWalletZipGate(onInvalidTaxClick);

  useEffect(() => {
    // Hydration is out of retries — collapse instead of spinning forever.
    if (isPaymentIntentUnavailable && !initializedRef.current) {
      setState('unavailable');
      onReadyRef.current?.(false);
    }
  }, [isPaymentIntentUnavailable, onReadyRef]);

  useEffect(() => {
    if (!cbInstance || !isPaymentIntentReady || initializedRef.current) return;
    initializedRef.current = true;
    let disposed = false;

    // Chargebee resolves the payment promise once per attempt; re-arm after a
    // dismissed sheet or a failed attempt so the button stays payable.
    const awaitNextPayment = (button: ChargebeeWalletButton) => {
      button
        .awaitPayment()
        .then(async (result) => {
          if (disposed) return;
          notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
          contextRef.current.actions.setIsExpressPaymentInProcess(true);
          await contextRef.current.actions.runPayment(
            { paymentIntent: result.paymentIntent },
            PaymentMethodEnum.GOOGLE_PAY
          );
          if (!disposed) awaitNextPayment(button);
        })
        .catch(async (error: unknown) => {
          if (disposed) return;
          if (isChargebeePaymentIntentAuthorizedError(error)) {
            await finalizeAlreadyAuthorizedWalletPayment(contextRef.current, PaymentMethodEnum.GOOGLE_PAY);
            if (!disposed) awaitNextPayment(button);
            return;
          }
          if (!isChargebeeUserClosedError(error)) {
            logError('Chargebee Google Pay payment failed', error);
            contextRef.current.actions.setError(tRef.current('somethingWentWrong'));
          }
          contextRef.current.actions.setIsExpressPaymentInProcess(false);
          awaitNextPayment(button);
        });
    };

    const init = async () => {
      try {
        const paymentIntent = contextRef.current.subscription?.paymentIntent;
        if (!paymentIntent) throw new Error('Missing Chargebee payment intent');
        const button = await chargebeeService.mountGooglePayButton({
          selector: `#${CHARGEBEE_WALLET_BUTTON_IDS.GOOGLE_PAY}`,
          paymentIntent,
        });
        if (disposed) return;
        buttonRef.current = button;
        setState('ready');
        onReadyRef.current?.(true);
        awaitNextPayment(button);
      } catch (error) {
        logError('Chargebee Google Pay unavailable', error);
        if (disposed) return;
        setState('unavailable');
        onReadyRef.current?.(false);
      }
    };

    void init();
    return () => {
      disposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbInstance, isPaymentIntentReady]);

  // A ZIP tax recalc updates the intent's amount SERVER-side under the same id,
  // so `useChargebeePaymentIntent` re-retrieves it and hands down a NEW object —
  // re-syncing on that identity alone covers the recalc. Fields-mode replacement
  // for v2's `paymentComponent.update({ paymentIntent: { id } })`.
  const paymentIntent = subscription?.paymentIntent;
  useEffect(() => {
    if (paymentIntent && buttonRef.current) {
      buttonRef.current.updatePaymentIntent(paymentIntent);
    }
  }, [paymentIntent]);

  if (state === 'unavailable') return null;

  return (
    <WalletButtonShell
      state={state}
      placeholder={<GooglePayButtonPlaceholder />}
      testId="wallet-btn-google-chargebee"
      usePlaceholderAsVisual
    >
      <div className="relative w-full h-12 min-h-12">
        <div
          id={CHARGEBEE_WALLET_BUTTON_IDS.GOOGLE_PAY}
          className="w-full h-12 min-h-12 rounded-lg overflow-hidden"
          data-testid="chargebee-google-pay-button"
          // Chargebee owns the button's click; capture it here purely for the
          // payment-click analytics every other wallet emits.
          onClickCapture={() =>
            trackAndNotifyPaymentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY })
          }
        />
        {isBlockedForZipCode && (
          <div
            data-testid="chargebee-google-pay-zip-blocker"
            className="absolute inset-0 w-full h-full z-[2147483647]"
            onPointerDown={blockClick}
            onClick={blockClick}
          />
        )}
      </div>
    </WalletButtonShell>
  );
};
